import React, { useState, useEffect } from "react";
import Header from "./Header";
import ReactPlayer from "react-player/vimeo";
import { Link, useLocation } from "react-router-dom";

const LandingSection = ({ data }) => {
  const location = useLocation();
  const [utahTime, setUtahTime] = useState("");
  const [isClient, setIsClient] = useState(false);
  const [playerWidth, setPlayerWidth] = useState("100vw");
  const [videoReady, setVideoReady] = useState(false); // <-- New state

  useEffect(() => {
    const updateWidth = () => {
      const width = window.innerWidth;

      if (width <= 640) {
        setPlayerWidth("300vh"); // full width on mobile
      } else if (width <= 1024) {
        setPlayerWidth("400vh"); // full width on tablet
      } else if (width <= 1660) {
        setPlayerWidth("200vw"); // full width on tablet
      } else {
        setPlayerWidth("100vw"); // full width on desktop
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    setIsClient(true);
    const updateTime = () => {
      const options = {
        timeZone: "America/Denver",
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      };
      const formatter = new Intl.DateTimeFormat("en-US", options);
      setUtahTime(formatter.format(new Date()));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const backgroundContent = data || "https://vimeo.com/1089657728";

  // Breadcrumb mapping for URL segments
  const breadcrumbMap = {
    // Utah at a Glance pages
    "utah-at-a-glance": "Utah at a Glance",
    "adventure-activities": "Adventure Activities",
    "charging-stations": "Utah Charging Stations",
    "utah-housing": "Utah Housing",
    "utah-counties": "Utah Counties",
    "demographics": "Demographics",
    "jobs": "Jobs",
    "unemployment-rate": "Unemployment Rate",
    "geothermal": "Geothermal",
    "airports": "Airports",
    "mlm": "MLM",
    "hospital": "Hospitals",
    "air-quality": "Air Quality",
    "zipcode": "Zip Codes",
    "finance": "Finance",
    "culture": "Culture",
    "places-to-visit": "Places to Visit",
    "agencies": "Federal Agencies",
    "utah-funfacts": "Utah Fun Facts",
    "sba-advocacy": "SBA Advocacy",
    "utah-governor": "Utah Governor",

    // Verticals pages
    "verticals": "Utah Verticals",
    "manufacturing": "Manufacturing",
    "information": "Information",
    "professional-scientific-and-technical-services":
      "Professional, Scientific, and Technical Services",
    "food-industry": "Food Industry",
    "transportation-and-warehousing": "Transportation and Warehousing",
    "architecture-and-construction": "Architecture and Construction",
    "construction": "Construction",
    "aerospace-and-defense": "Aerospace and Defense",
  };

  // Generate breadcrumb from URL if not provided
  const generateBreadcrumbFromUrl = () => {
    const pathSegments = location.pathname
      .split("/")
      .filter((segment) => segment !== "");

    // Don't show breadcrumb for parent pages (only one segment) or home page
    if (pathSegments.length <= 1) return null;

    const breadcrumbParts = [];
    const breadcrumbLinkParts = [];
    let currentPath = "";

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const displayName =
        breadcrumbMap[segment] ||
        segment.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

      breadcrumbParts.push(displayName);
      if (index < pathSegments.length - 1) {
        breadcrumbLinkParts.push(currentPath);
      }
    });

    return {
      breadcrumb: breadcrumbParts.join(" >> "),
      breadcrumbLinks: breadcrumbLinkParts,
    };
  };

  const renderBreadcrumb = () => {
    const breadcrumbData = generateBreadcrumbFromUrl();
    if (!breadcrumbData) return null;

    if (
      breadcrumbData.breadcrumbLinks &&
      breadcrumbData.breadcrumbLinks.length > 0
    ) {
      const parts = breadcrumbData.breadcrumb.split(" >> ");
      return (
        <div
          className="breadcrumb"
          style={{
            fontSize: "14px",
            marginBottom: "10px",
            color: "white",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {parts.map((part, index) => (
            <span key={index}>
              {index < parts.length - 1 ? (
                <Link
                  to={breadcrumbData.breadcrumbLinks[index] || "#"}
                  style={{
                    color: "white",
                    textDecoration: "underline",
                    textShadow: "2px 2px 4px rgba(0,0,0,1)",
                  }}
                  onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
                  onMouseOut={(e) => (e.target.style.textDecoration = "underline")}
                >
                  {part}
                </Link>
              ) : (
                <span style={{ textShadow: "2px 2px 4px rgba(0,0,0,1)" }}>
                  {part}
                </span>
              )}
              {index < parts.length - 1 && <span> >>&nbsp;</span>}
            </span>
          ))}
        </div>
      );
    }

    return (
      <div
        className="breadcrumb"
        style={{
          fontSize: "14px",
          marginBottom: "10px",
          color: "white",
          textAlign: "center",
          textShadow: "2px 2px 4px rgba(0,0,0,1)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {breadcrumbData.breadcrumb}
      </div>
    );
  };

  return (
    <div className="relative w-full min-h-screen h-screen overflow-hidden flex flex-col justify-between text-white">
      {/* Video or placeholder */}
      <div className="absolute inset-0 z-0">
        {!videoReady && (
          <img
            src="/img/placeholder1.webp" // Make sure this image exists
            alt="Loading..."
            className="w-full h-full object-cover"
          />
        )}

        {isClient && (
          <ReactPlayer
            url={backgroundContent}
            playing
            loop
            muted
            width={playerWidth}
            height="100vh"
            style={{
              position: "absolute",
              minHeight: "100vh",
              top: 0,
              left: 0,
              pointerEvents: "none",
            }}
            playsinline
            onStart={() => setVideoReady(true)} // <-- Hide image when ready
            config={{
              vimeo: {
                playerOptions: {
                  background: true,
                  responsive: true,
                },
              },
            }}
          />
        )}
      </div>

      <div className="absolute inset-0 bg-black/0 z-1" />

      <div className="relative z-2">
        <Header />
      </div>

      <div
        className="flex justify-center mt-[5rem] relative"
        style={{ textShadow: "2px 2px 4px rgba(0,0,0,1)" }}
      ></div>

      <div
        className="landing_content "
        style={{ textShadow: "2px 2px 4px rgba(0,0,0,1)" }}
      >
        {renderBreadcrumb()}
        <h1>Adventure Activities</h1>
        <p>[SCROLL TO SEE]</p>
      </div>
    </div>
  );
};

export default LandingSection;
